import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Paper,
  Card,
  CardContent,
  CardActions,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Code as CodeIcon,
  Speed as SpeedIcon,
  Launch as LaunchIcon,
  Article as ArticleIcon,
  Rocket as RocketIcon,
  Security as SecurityIcon,
  Devices as DevicesIcon,
} from '@mui/icons-material';
import useAuth from '../../features/auth/hooks/useAuth';
import Coverflow from './Coverflow';

// Screen captures for coverflow gallery
const screenCaptures = [
  { src: '/v2screens/multiagent.png', title: 'Multi-Agent System', subtitle: 'Orchestrated AI Collaboration' },
  { src: '/v2screens/cli.png', title: 'Terminal Interface', subtitle: 'Powerful Command Line' },
  { src: '/v2screens/coding.png', title: 'Code Generation', subtitle: 'Production-Ready Output' },
  { src: '/v2screens/graphics.png', title: 'Visual Interface', subtitle: 'Modern Web UI' },
];

const HomePage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuthenticated } = useAuth();

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 10, md: 16 },
          position: 'relative',
          overflow: 'hidden',
          minHeight: { xs: '60vh', md: '70vh' },
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h1"
              component="h1"
              gutterBottom
              fontWeight="bold"
              sx={{
                fontSize: { xs: '2.5rem', sm: '3rem', md: '4rem' },
                lineHeight: 1.1,
                mb: 3,
              }}
            >
              The AI Agent That Thinks Like You
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 5,
                opacity: 0.9,
                fontSize: { xs: '1.1rem', md: '1.35rem' },
                maxWidth: 800,
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              Meet Autopilot One: A multi-agent, multi-interface AI system that builds secure, production-ready code following your project's practices and coding style—making it easier to trust and deploy.
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 5, justifyContent: 'center' }}>
              <Box sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                px: 2,
                py: 1,
                borderRadius: 2,
                fontSize: '0.875rem'
              }}>
                <DevicesIcon fontSize="small" />
                Multi-Interface System
              </Box>
              <Box sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                px: 2,
                py: 1,
                borderRadius: 2,
                fontSize: '0.875rem'
              }}>
                <SecurityIcon fontSize="small" />
                Security-First Design
              </Box>
              <Box sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                px: 2,
                py: 1,
                borderRadius: 2,
                fontSize: '0.875rem'
              }}>
                <RocketIcon fontSize="small" />
                Fast Deployment
              </Box>
            </Box>

            <Box display="flex" gap={2} flexWrap="wrap" justifyContent="center">
              {isAuthenticated ? (
                <Button
                  variant="contained"
                  size="large"
                  component={RouterLink}
                  to="/dashboard"
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    px: 5,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    '&:hover': { bgcolor: 'grey.100' }
                  }}
                >
                  Go to Dashboard
                </Button>
              ) : (
                <>
                  <Button
                    variant="contained"
                    size="large"
                    component={RouterLink}
                    to="/register"
                    sx={{
                      bgcolor: 'white',
                      color: 'primary.main',
                      px: 5,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      '&:hover': { bgcolor: 'grey.100' }
                    }}
                  >
                    Start Building Free
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    href="https://www.npmjs.com/package/@loxia-labs/loxia-autopilot-one"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      borderColor: 'white',
                      color: 'white',
                      px: 5,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      '&:hover': { borderColor: 'grey.300', bgcolor: 'rgba(255,255,255,0.1)' }
                    }}
                  >
                    View Documentation
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Screenshot Gallery Section */}
      <Box
        sx={{
          background: theme.palette.mode === 'light'
            ? 'linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%)'
            : 'linear-gradient(180deg, #0a0a0f 0%, #1a1a2e 50%, #0a0a0f 100%)',
          py: { xs: 6, md: 10 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Ambient light effect for dark mode */}
        <Box
          sx={{
            display: theme.palette.mode === 'dark' ? 'block' : 'none',
            position: 'absolute',
            top: -200,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 800,
            height: 500,
            background: 'radial-gradient(ellipse, rgba(102, 126, 234, 0.12) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />

        <Container maxWidth="lg">
          <Box textAlign="center" mb={4}>
            <Typography
              variant="h3"
              component="h2"
              gutterBottom
              fontWeight="bold"
              sx={{
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #f0ece2 0%, #b8a9c9 50%, #f0ece2 100%)'
                  : 'inherit',
                WebkitBackgroundClip: theme.palette.mode === 'dark' ? 'text' : 'inherit',
                WebkitTextFillColor: theme.palette.mode === 'dark' ? 'transparent' : 'inherit',
                backgroundClip: theme.palette.mode === 'dark' ? 'text' : 'inherit',
              }}
            >
              See It In Action
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              Experience the power of Autopilot One through our modern interfaces
            </Typography>
          </Box>

          <Coverflow images={screenCaptures} autoPlay autoPlayInterval={5000} />
        </Container>
      </Box>

      {/* How Autopilot One Works */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box textAlign="center" mb={6}>
          <Typography variant="h3" component="h2" gutterBottom fontWeight="bold">
            Multi-Agent Intelligence, Unified Experience
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
            Autopilot One orchestrates specialized AI agents working in harmony—each expert in their domain—to deliver secure,
            production-ready code that follows your project's conventions and best practices.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
              <Box sx={{
                bgcolor: 'primary.main',
                width: 80,
                height: 80,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2
              }}>
                <SecurityIcon sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              <Typography variant="h5" gutterBottom fontWeight="600">
                Security-First Development
              </Typography>
              <Typography color="text.secondary">
                Every line of code is built with security in mind. Our multi-agent system automatically
                implements best practices, vulnerability prevention, and secure coding patterns to protect your applications.
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
              <Box sx={{
                bgcolor: 'secondary.main',
                width: 80,
                height: 80,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2
              }}>
                <CodeIcon sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              <Typography variant="h5" gutterBottom fontWeight="600">
                Maintains Your Standards
              </Typography>
              <Typography color="text.secondary">
                Analyzes and adapts to your project's existing coding style, conventions, and best practices—
                delivering code that feels native to your codebase, making it easier to trust and integrate.
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
              <Box sx={{
                bgcolor: 'success.main',
                width: 80,
                height: 80,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2
              }}>
                <SpeedIcon sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              <Typography variant="h5" gutterBottom fontWeight="600">
                Fast, Production-Ready Deployment
              </Typography>
              <Typography color="text.secondary">
                From concept to deployed application in minutes. Generate production-ready code with
                built-in testing, documentation, and deployment configurations—ready to ship immediately.
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Featured Demo Section */}
      <Box sx={{ bgcolor: theme.palette.mode === 'light' ? 'grey.50' : 'background.paper', py: 8 }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Typography variant="h3" component="h2" gutterBottom fontWeight="bold">
              Featured Demo: PC Part Picker
            </Typography>
            <Typography variant="h6" color="text.secondary">
              A comprehensive PC building and component comparison platform built with Autopilot One
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={8} lg={6}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ position: 'relative' }}>
                  <Box
                    component="img"
                    src="/demo-thumbnail.png"
                    alt="PC Part Picker Demo"
                    sx={{
                      width: '100%',
                      height: 300,
                      objectFit: 'cover',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  />
                </Box>
                
                <CardContent sx={{ flexGrow: 1, p: 4 }}>
                  <Typography variant="h5" gutterBottom fontWeight="600">
                    PC Assembly & Comparison Platform
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    A fully functional platform for building custom PCs and comparing components. 
                    Features real-time compatibility checking, price comparisons, and detailed specifications.
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="caption" color="primary.main" fontWeight="600" sx={{ display: 'block', mb: 1 }}>
                      BUILT WITH AUTOPILOT ONE USING PROMPT:
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      fontStyle: 'italic', 
                      bgcolor: theme.palette.mode === 'light' ? 'grey.100' : 'grey.800', 
                      color: 'text.primary',
                      p: 2, 
                      borderRadius: 1,
                      fontSize: '0.9rem',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}>
                      "Create a PC assembly and part comparison platform with component compatibility checking, 
                      price tracking, and build optimization recommendations"
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                    {['React', 'Vite', 'JavaScript', 'CSS', 'Component API'].map((tech) => (
                      <Box key={tech} sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        px: 1.5,
                        py: 0.5,
                        fontSize: '0.8rem',
                        color: 'text.secondary',
                        bgcolor: 'background.paper'
                      }}>
                        {tech}
                      </Box>
                    ))}
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box sx={{ fontSize: '1.2rem' }}>🧠</Box>
                    <Typography variant="body2">
                      Real-time compatibility checking for all components
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box sx={{ fontSize: '1.2rem' }}>⚖️</Box>
                    <Typography variant="body2">
                      Advanced price and performance comparison tools
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box sx={{ fontSize: '1.2rem' }}>🤖</Box>
                    <Typography variant="body2">
                      AI-powered build optimization recommendations
                    </Typography>
                  </Box>
                </CardContent>
                
                <CardActions sx={{ p: 3, pt: 0, gap: 2 }}>
                  <Button 
                    variant="contained" 
                    size="large" 
                    startIcon={<LaunchIcon />} 
                    href="https://part-picker.demos.loxia.ai" 
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ flexGrow: 1 }}
                  >
                    Try Live Demo
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Grid>

          {/* GPT Marketplace Tool */}
          <Box sx={{ mt: 8, textAlign: 'center' }}>
            <Paper sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Box
                  component="img"
                  src="/gpt-lox-arch.png"
                  alt="Loxia Prompt Architect GPT"
                  sx={{
                    height: 80,
                    objectFit: 'contain'
                  }}
                />
              </Box>
              <Typography variant="h5" gutterBottom fontWeight="600">
                Need Help Crafting Your Prompt?
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Use our free <strong>Loxia Prompt Architect</strong> GPT to create comprehensive,
                detailed prompts that help Autopilot One understand exactly what you want to build.
              </Typography>
              <Button
                variant="outlined"
                size="large"
                href="https://chatgpt.com/g/g-68330f05037c819191af03ee3983c1f3-loxia-prompt-architect"
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<Box sx={{ fontSize: '1.2rem' }}>🤖</Box>}
                sx={{ mt: 2 }}
              >
                Try Loxia Prompt Architect (Free)
              </Button>
            </Paper>
          </Box>
        </Container>
      </Box>

      {/* Documentation Hub */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h3" component="h2" gutterBottom fontWeight="bold">
              Ready to Start Building?
            </Typography>
            <Typography variant="h6" color="text.secondary" paragraph>
              Get started with Autopilot One in minutes. Modern Web UI + powerful CLI for any workflow.
            </Typography>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom color="primary.main">
                Quick Start:
              </Typography>
              <Box component="ol" sx={{ pl: 2 }}>
                <Typography component="li" sx={{ mb: 1.5 }}>
                  <strong>Create Account:</strong> Register at <code>autopilot.loxia.ai</code>
                </Typography>
                <Typography component="li" sx={{ mb: 1.5 }}>
                  <strong>Install:</strong> <code>npm install -g @loxia-labs/loxia-autopilot-one</code>
                </Typography>
                <Typography component="li" sx={{ mb: 1.5 }}>
                  <strong>Get API Key:</strong> Navigate to API Keys section and create a new key
                </Typography>
                <Typography component="li" sx={{ mb: 1.5 }}>
                  <strong>Launch:</strong> Run <code>loxia web</code> for Web UI or <code>loxia plus-terminal</code> for Terminal UI
                </Typography>
                <Typography component="li" sx={{ mb: 1.5 }}>
                  <strong>Configure:</strong> Go to Settings and paste your API key
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                href="https://www.npmjs.com/package/@loxia-labs/loxia-autopilot-one"
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<ArticleIcon />}
                sx={{ mb: 1 }}
              >
                View Documentation
              </Button>
              <Button
                variant="outlined"
                size="large"
                href="https://www.npmjs.com/package/@loxia-labs/loxia-autopilot-one#-quick-start"
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<CodeIcon />}
                sx={{ mb: 1 }}
              >
                Quick Start Guide
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              sx={{
                bgcolor: '#1e1e1e',
                color: '#d4d4d4',
                p: 3,
                borderRadius: 2,
                fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                fontSize: '0.85rem',
                border: '1px solid #333',
              }}
            >
              <Typography component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap' }}>
{`# Install Autopilot One
$ npm install -g @loxia-labs/loxia-autopilot-one

# Verify installation
$ loxia --version
Loxia Autopilot One v1.0.3

# Launch Web UI (opens http://localhost:8080)
$ loxia web

# Or launch Terminal UI
$ loxia plus-terminal

# CLI Reference
loxia web             # Web UI in browser
loxia terminal        # Terminal UI only
loxia plus-terminal   # Server + Terminal UI
loxia --port 3000     # Custom port`}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Pricing Plans */}
      <Box sx={{ bgcolor: theme.palette.mode === 'light' ? 'grey.50' : 'background.paper', py: 10 }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Typography variant="h3" component="h2" gutterBottom fontWeight="bold">
              Choose Your Plan
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              Flexible pricing options to fit your needs, from individual developers to enterprise teams.
            </Typography>
          </Box>

          <Grid container spacing={3} justifyContent="center">
            {/* Pay-As-You-Go */}
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  border: '2px solid',
                  borderColor: 'primary.main',
                  boxShadow: '0 4px 20px rgba(102, 126, 234, 0.25)',
                }}
              >
                <Box
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    py: 0.5,
                    px: 2,
                    position: 'absolute',
                    top: 12,
                    right: -8,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    borderRadius: '4px 0 0 4px',
                    boxShadow: 2,
                  }}
                >
                  AVAILABLE NOW
                </Box>
                <CardContent sx={{ flexGrow: 1, p: 3, pt: 5 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Pay-As-You-Go
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary.main" gutterBottom>
                    $0
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    + usage costs
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Perfect for getting started. Pay only for what you use.
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                    <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>$5 free starting credit</Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Per-token pricing</Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>All AI models</Typography>
                    <Typography component="li" variant="body2">No commitment</Typography>
                  </Box>
                </CardContent>
                <CardActions sx={{ p: 3, pt: 0 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    component={RouterLink}
                    to="/register"
                  >
                    Get Started
                  </Button>
                </CardActions>
              </Card>
            </Grid>

            {/* Personal */}
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  opacity: 0.85,
                }}
              >
                <Box
                  sx={{
                    bgcolor: 'warning.main',
                    color: 'warning.contrastText',
                    py: 0.5,
                    px: 2,
                    position: 'absolute',
                    top: 12,
                    right: -8,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    borderRadius: '4px 0 0 4px',
                    boxShadow: 2,
                  }}
                >
                  LAUNCHING SOON
                </Box>
                <CardContent sx={{ flexGrow: 1, p: 3, pt: 5 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Personal
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="text.secondary" gutterBottom>
                    TBD
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    per month
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    For individual developers and hobbyists.
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                    <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Monthly token allowance</Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Priority support</Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Advanced features</Typography>
                    <Typography component="li" variant="body2">Usage analytics</Typography>
                  </Box>
                </CardContent>
                <CardActions sx={{ p: 3, pt: 0 }}>
                  <Button variant="outlined" fullWidth disabled>
                    Coming Soon
                  </Button>
                </CardActions>
              </Card>
            </Grid>

            {/* Pro */}
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  opacity: 0.85,
                }}
              >
                <Box
                  sx={{
                    bgcolor: 'warning.main',
                    color: 'warning.contrastText',
                    py: 0.5,
                    px: 2,
                    position: 'absolute',
                    top: 12,
                    right: -8,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    borderRadius: '4px 0 0 4px',
                    boxShadow: 2,
                  }}
                >
                  LAUNCHING SOON
                </Box>
                <CardContent sx={{ flexGrow: 1, p: 3, pt: 5 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Pro
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="text.secondary" gutterBottom>
                    TBD
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    per month
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    For professional developers and freelancers.
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                    <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Higher token limits</Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Premium AI models</Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Priority queue</Typography>
                    <Typography component="li" variant="body2">API access</Typography>
                  </Box>
                </CardContent>
                <CardActions sx={{ p: 3, pt: 0 }}>
                  <Button variant="outlined" fullWidth disabled>
                    Coming Soon
                  </Button>
                </CardActions>
              </Card>
            </Grid>

            {/* Teams */}
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  opacity: 0.85,
                }}
              >
                <Box
                  sx={{
                    bgcolor: 'warning.main',
                    color: 'warning.contrastText',
                    py: 0.5,
                    px: 2,
                    position: 'absolute',
                    top: 12,
                    right: -8,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    borderRadius: '4px 0 0 4px',
                    boxShadow: 2,
                  }}
                >
                  LAUNCHING SOON
                </Box>
                <CardContent sx={{ flexGrow: 1, p: 3, pt: 5 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Teams
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="text.secondary" gutterBottom>
                    TBD
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    per user/month
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    For small to medium teams collaborating together.
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                    <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Team workspaces</Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Shared agents</Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Admin controls</Typography>
                    <Typography component="li" variant="body2">Usage reports</Typography>
                  </Box>
                </CardContent>
                <CardActions sx={{ p: 3, pt: 0 }}>
                  <Button variant="outlined" fullWidth disabled>
                    Coming Soon
                  </Button>
                </CardActions>
              </Card>
            </Grid>

            {/* Enterprise */}
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  opacity: 0.85,
                }}
              >
                <Box
                  sx={{
                    bgcolor: 'warning.main',
                    color: 'warning.contrastText',
                    py: 0.5,
                    px: 2,
                    position: 'absolute',
                    top: 12,
                    right: -8,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    borderRadius: '4px 0 0 4px',
                    boxShadow: 2,
                  }}
                >
                  LAUNCHING SOON
                </Box>
                <CardContent sx={{ flexGrow: 1, p: 3, pt: 5 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Enterprise
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="text.secondary" gutterBottom>
                    Custom
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    tailored pricing
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    For large organizations with custom requirements.
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                    <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Unlimited usage</Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Custom deployment</Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>SLA & support</Typography>
                    <Typography component="li" variant="body2">SSO & security</Typography>
                  </Box>
                </CardContent>
                <CardActions sx={{ p: 3, pt: 0 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    href="mailto:contact@loxia.ai"
                  >
                    Contact Us
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Final CTA */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box textAlign="center">
          <Typography variant="h3" component="h2" gutterBottom fontWeight="bold">
            Start Building with Autopilot One Today
          </Typography>
          <Typography variant="h6" color="text.secondary" paragraph>
            Join developers who are shipping secure, production-ready applications faster with multi-agent AI intelligence.
          </Typography>
          
          <Box display="flex" justifyContent="center" gap={2} flexWrap="wrap">
            <Button
              variant="contained"
              size="large"
              component={RouterLink}
              to={isAuthenticated ? "/dashboard" : "/register"}
              sx={{ px: 4, py: 2, fontSize: '1.1rem' }}
            >
              {isAuthenticated ? "Go to Dashboard" : "Get Started Free"}
            </Button>
            <Button
              variant="outlined"
              size="large"
              href="https://www.npmjs.com/package/@loxia-labs/loxia-autopilot-one"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ px: 4, py: 2, fontSize: '1.1rem' }}
            >
              Read the Docs
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default HomePage;